import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, Rocket, HelpCircle, X, Gift, StickyNote } from "lucide-react";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { useLocation, useNavigate } from "react-router-dom";
import { SearchPopover } from "./search/SearchPopover";
import NotificationsPopover from "./NotificationsPopover";
import UserMenu from "./user/UserMenu";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import { useHivePadNotifications } from "@/hooks/useHivePadNotifications";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

interface TopBarProps {
  onSearch?: (query: string) => void;
  searchPlaceholder?: string;
  className?: string;
  showSearch?: boolean;
  sidebarId?: string;
  hideMenuButton?: boolean;
  onGettingStartedClick?: () => void;
}

const HELP_CENTER_URL = 'https://hivetivity.tawk.help/';
const SUBMIT_TICKET_URL = 'https://hivetivity.tawk.help/';

function HelpDeskPopover() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Help & Support"
          className="relative"
        >
          <HelpCircle className="h-5 w-5 text-primary" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="p-4 min-w-[240px] max-w-[320px] rounded-xl shadow-lg bg-gradient-to-br from-white via-slate-50 to-slate-100 border border-slate-200">
        <div className="flex flex-col items-center gap-2 text-center">
          <HelpCircle className="h-7 w-7 text-primary mb-1" />
          <div className="font-semibold text-lg">Need Help?</div>
          <div className="text-sm text-muted-foreground mb-2">
            Our support team is here for you.<br />Submit a ticket or browse our knowledge base.
          </div>
          <Button
            variant="default"
            className="w-full rounded-lg font-semibold shadow"
            onClick={() => window.open(SUBMIT_TICKET_URL, '_blank')}
          >
            Submit a Ticket
          </Button>
          <Button
            variant="ghost"
            className="w-full rounded-lg font-medium text-primary hover:bg-primary/10"
            onClick={() => window.open(HELP_CENTER_URL, '_blank')}
          >
            Visit Knowledge Base
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function TopBar({
  onSearch,
  searchPlaceholder = "Search anything...",
  className,
  showSearch = true,
  sidebarId,
  hideMenuButton = false,
  onGettingStartedClick,
}: TopBarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { userRole, user } = useAuth();
  const { counts: hivePadCounts, hasOverdue } = useHivePadNotifications();
  const [showGettingStarted, setShowGettingStarted] = useState(true);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  
  // Check if we're in the parent portal context
  const isParentPortal = location.pathname.includes('/parent-portal');
  const isBizAdmin = userRole === 'bizadmin' || userRole === 'owner';
  
  // Check localStorage for getting started preference
  useEffect(() => {
    const hidden = localStorage.getItem(`hideGettingStarted_${user?.id}`);
    if (hidden === 'true') {
      setShowGettingStarted(false);
    }
  }, [user?.id]);
  
  const handleDismissGettingStarted = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowConfirmDialog(true);
  };
  
  const handleConfirmHide = () => {
    setShowGettingStarted(false);
    setShowConfirmDialog(false);
    if (user?.id) {
      localStorage.setItem(`hideGettingStarted_${user?.id}`, 'true');
    }
  };
  
  const handleCancelHide = () => {
    setShowConfirmDialog(false);
  };
  
  const toggleSidebar = () => {
    if (!sidebarId) return;
    
    const sidebar = document.getElementById(sidebarId);
    if (sidebar) {
      sidebar.classList.toggle("translate-x-0");
      sidebar.classList.toggle("-translate-x-full");
    }
  };
  
  return (
    <header className={cn("sticky top-0 z-30 flex h-14 items-center border-b bg-background px-4 sm:static", className)}>
      {/* For Parent Portal Mobile - Even spacing across full width */}
      {isParentPortal && isMobile && !hideMenuButton ? (
        <>
          {/* Hamburger all the way left */}
          <Button variant="ghost" size="icon" className="shrink-0" onClick={toggleSidebar}>
            <Menu className="h-5 w-5" />
            <span className="sr-only">Toggle Menu</span>
          </Button>
          
          {/* Flex spacer to push other icons to the right */}
          <div className="flex-1" />
          
          {/* Evenly spaced icons on the right */}
          <div className="flex items-center gap-6">
            <NotificationsPopover />
          </div>
        </>
      ) : (
        <>
          {/* Left Section */}
          <div className="flex items-center gap-4">
            {isMobile && !hideMenuButton && <Button variant="ghost" size="icon" className="shrink-0" onClick={toggleSidebar}>
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle Menu</span>
            </Button>}
            
            {showSearch && !isParentPortal && (
              <div className="flex-1 max-w-xl">
                <SearchPopover />
              </div>
            )}
          </div>
          
          {/* Right Section */}
          <div className="flex items-center gap-3 ml-auto">
            {isBizAdmin && !isParentPortal && onGettingStartedClick && showGettingStarted && (
              <>
                <div className="hidden lg:flex items-center gap-2 mr-2 animate-fade-in">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onGettingStartedClick}
                    aria-label="Getting Started"
                    className="relative flex items-center gap-2 border-primary/20 bg-primary/5 hover:bg-primary/10 hover:border-primary/30 text-primary font-semibold transition-all duration-200 hover:scale-105 pr-8"
                  >
                    <Rocket className="h-4 w-4 animate-pulse" />
                    <span>Getting Started</span>
                    <span className="absolute right-2 top-1/2 -translate-y-1/2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            onClick={handleDismissGettingStarted}
                            className="h-5 w-5 flex items-center justify-center rounded-full hover:bg-muted transition"
                            aria-label="Hide forever"
                            title="Hide forever"
                            tabIndex={0}
                            type="button"
                            style={{ outline: 'none', border: 'none', background: 'none', padding: 0, margin: 0 }}
                          >
                            <X className="h-4 w-4 text-muted-foreground hover:text-primary transition" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="p-2 text-xs rounded-md shadow-md">
                          Hide forever
                        </PopoverContent>
                      </Popover>
                    </span>
                  </Button>
                </div>
                <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                  <DialogContent className="max-w-sm rounded-xl">
                    <DialogHeader>
                      <DialogTitle>Hide Getting Started tips forever?</DialogTitle>
                    </DialogHeader>
                    <div className="text-muted-foreground text-sm mb-4">
                      Are you sure you want to hide Getting Started tips forever? This can be reset in your profile settings.
                    </div>
                    <DialogFooter className="flex gap-2 justify-end">
                      <Button variant="ghost" onClick={handleCancelHide}>Cancel</Button>
                      <Button variant="destructive" onClick={handleConfirmHide}>Hide Forever</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </>
            )}
            <div className="flex items-center gap-3">
              {!isParentPortal && (
                <>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="What's New"
                          className="relative"
                          onClick={() => navigate('/admin/whats-new')}
                        >
                          <Gift className="h-5 w-5 text-primary" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        What's New
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="HivePad"
                          className="relative"
                          onClick={() => navigate('/admin/hivepad')}
                        >
                          <StickyNote className="h-5 w-5 text-primary" />
                          {hivePadCounts.total > 0 && (
                            <span 
                              className={`absolute -top-1 -right-1 h-5 w-5 rounded-full text-xs font-medium flex items-center justify-center ${
                                hasOverdue 
                                  ? 'bg-red-500 text-white' 
                                  : 'bg-blue-500 text-white'
                              }`}
                            >
                              {hivePadCounts.total > 99 ? '99+' : hivePadCounts.total}
                            </span>
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        HivePad
                        {hivePadCounts.total > 0 && (
                          <div className="text-xs mt-1">
                            {hasOverdue ? `${hivePadCounts.overdue} overdue` : `${hivePadCounts.incomplete} incomplete`}
                          </div>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </>
              )}
              <NotificationsPopover />
              {!isParentPortal && <HelpDeskPopover />}
              {!isParentPortal && <UserMenu isParentPortal={isParentPortal} />}
            </div>
          </div>
        </>
      )}
    </header>
  );
}
